import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import Swal from "sweetalert2";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showModal, hideModal, installModalFallback } from "./modalHelper";
import TacheFormModal, { TACHE_MODAL_ID } from "./TacheFormModal";
import TacheRow, { TacheTableHead } from "./TacheRow";
import { API, unwrap, apiMessage, toast, formatDate, formatAmount, toDateInput, ProgressBar } from "./projetsCommon";

const PHASE_MODAL = "phaseFormModal";
const emptyPhase = { nom: "", dateDebut: "", dateFin: "", budgetPrevu: "", description: "" };

const phaseSchema = Yup.object({
    nom: Yup.string().trim().required("Le nom est obligatoire").max(150, "150 caractères maximum"),
    dateFin: Yup.string().nullable().test("apres", "La fin doit suivre le début", function (v) {
        const { dateDebut } = this.parent;
        return !v || !dateDebut || v >= dateDebut;
    }),
    budgetPrevu: Yup.number().typeError("Budget invalide").nullable().transform((v, o) => (o === "" ? null : v)).min(0, "Budget invalide"),
});

/** Onglet « Phases & tâches » de la fiche projet. */
const ProjetPlanning = ({ projetId, employes, onAvancementChange }) => {
    const [planning, setPlanning] = useState(null);
    const [collapsed, setCollapsed] = useState(new Set());
    const [editingPhase, setEditingPhase] = useState(null);
    const [editingTache, setEditingTache] = useState(null);
    const [defaultPhaseId, setDefaultPhaseId] = useState("");
    const [openKey, setOpenKey] = useState(0);

    useEffect(() => installModalFallback(), []);
    useEffect(() => { load(); }, [projetId]); // eslint-disable-line react-hooks/exhaustive-deps

    const load = (notify) =>
        axiosInstance.get(API.planning(projetId))
            .then((res) => {
                setPlanning(unwrap(res));
                if (notify && onAvancementChange) onAvancementChange();   // l'avancement du projet a pu changer
            })
            .catch((error) => toast("error", apiMessage(error, "Impossible de charger le planning")));

    // ---------- Phases ----------
    const phaseFormik = useFormik({
        initialValues: emptyPhase,
        validationSchema: phaseSchema,
        onSubmit: async (v, { setErrors }) => {
            const payload = {
                nom: v.nom.trim(), dateDebut: v.dateDebut || null, dateFin: v.dateFin || null,
                budgetPrevu: v.budgetPrevu === "" || v.budgetPrevu === null ? null : Number(v.budgetPrevu),
                description: v.description?.trim() || null,
            };
            try {
                if (editingPhase) await axiosInstance.put(API.phase(projetId, editingPhase.id), payload);
                else await axiosInstance.post(API.phases(projetId), payload);
                hideModal(PHASE_MODAL);
                toast("success", editingPhase ? "Phase modifiée" : "Phase créée");
                load();
            } catch (error) {
                if (error.response?.data?.errors) setErrors(error.response.data.errors);
                else toast("error", apiMessage(error, "Enregistrement échoué"));
            }
        },
    });

    const openPhase = (p) => {
        setEditingPhase(p || null);
        phaseFormik.resetForm({
            values: p ? {
                nom: p.nom, dateDebut: toDateInput(p.dateDebut), dateFin: toDateInput(p.dateFin),
                budgetPrevu: p.budgetPrevu ?? "", description: p.description ?? "",
            } : emptyPhase,
        });
        showModal(PHASE_MODAL);
    };

    const movePhase = async (p, dir) => {
        try { await axiosInstance.put(API.movePhase(projetId, p.id, dir)); load(); }
        catch (error) { toast("error", apiMessage(error, "Déplacement impossible")); }
    };

    const deletePhase = (p) => {
        Swal.fire({
            title: `Supprimer la phase « ${p.nom} » ?`,
            text: p.nbTaches ? `Ses ${p.nbTaches} tâche(s) seront conservées « sans phase ».` : "",
            icon: "warning", showCancelButton: true, confirmButtonText: "Supprimer", cancelButtonText: "Annuler",
        }).then(async (r) => {
            if (!r.isConfirmed) return;
            try { await axiosInstance.delete(API.phase(projetId, p.id)); toast("success", "Phase supprimée"); load(); }
            catch (error) { toast("error", apiMessage(error, "Suppression échouée")); }
        });
    };

    // ---------- Tâches ----------
    const openTache = (t, phaseId) => {
        setEditingTache(t || null);
        setDefaultPhaseId(phaseId ?? "");
        setOpenKey((k) => k + 1);
        showModal(TACHE_MODAL_ID);
    };

    const changeStatut = async (t, statut) => {
        try {
            await axiosInstance.put(API.tacheSuivi(projetId, t.id), { statut });
            load(true);
        } catch (error) { toast("error", apiMessage(error, "Mise à jour échouée")); }
    };

    const deleteTache = (t) => {
        Swal.fire({
            title: `Supprimer la tâche « ${t.titre} » ?`, icon: "warning",
            showCancelButton: true, confirmButtonText: "Supprimer", cancelButtonText: "Annuler",
        }).then(async (r) => {
            if (!r.isConfirmed) return;
            try { await axiosInstance.delete(API.tache(projetId, t.id)); toast("success", "Tâche supprimée"); load(true); }
            catch (error) { toast("error", apiMessage(error, "Suppression échouée")); }
        });
    };

    const toggle = (key) => {
        const next = new Set(collapsed);
        next.has(key) ? next.delete(key) : next.add(key);
        setCollapsed(next);
    };

    if (!planning) return <p>Chargement...</p>;

    const renderTaches = (taches, phaseId) => (
        <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
                <TacheTableHead />
                <tbody>
                    {taches.length === 0 && <tr><td colSpan="8" className="text-center text-muted py-3">Aucune tâche</td></tr>}
                    {taches.map((t) => <TacheRow key={t.id} t={t} onStatut={changeStatut} onEdit={(x) => openTache(x)} onDelete={deleteTache} />)}
                </tbody>
            </table>
            <div className="p-2">
                <button className="btn btn-sm btn-link" onClick={() => openTache(null, phaseId)}><i className="fas fa-plus mr-1"></i>Ajouter une tâche</button>
            </div>
        </div>
    );

    const pi = invalid => (phaseFormik.touched[invalid] && phaseFormik.errors[invalid] ? "is-invalid" : "");

    return (
        <>
            {/* ---- Résumé ---- */}
            <div className="card mb-3">
                <div className="card-body d-flex flex-wrap align-items-center">
                    <div className="mr-4 mb-2" style={{ minWidth: 220 }}>
                        <div className="gp-info-label">Avancement du projet (calculé)</div>
                        <ProgressBar value={planning.avancement} />
                    </div>
                    <div className="mr-4 mb-2"><div className="gp-info-label">Tâches</div><strong>{planning.nbTerminees} / {planning.nbTaches}</strong> terminées</div>
                    <div className="mr-4 mb-2"><div className="gp-info-label">En retard</div><strong className={planning.nbEnRetard ? "text-danger" : ""}>{planning.nbEnRetard}</strong></div>
                    <div className="mr-4 mb-2"><div className="gp-info-label">Heures</div><strong>{planning.heuresReelles}</strong> / {planning.heuresEstimees} h</div>
                    <div className="mr-4 mb-2"><div className="gp-info-label">Main-d'œuvre</div><strong>{formatAmount(planning.coutMainOeuvre)}</strong></div>
                    <div className="ml-auto mb-2">
                        <button className="btn btn-outline-primary mr-2" onClick={() => openPhase(null)}><i className="fas fa-layer-group mr-1"></i>Nouvelle phase</button>
                        <button className="btn btn-primary" onClick={() => openTache(null, "")}><i className="fas fa-plus mr-1"></i>Nouvelle tâche</button>
                    </div>
                </div>
            </div>

            {planning.phases.length === 0 && planning.tachesSansPhase.length === 0 && (
                <div className="text-center text-muted py-4">
                    <i className="fas fa-tasks fa-2x mb-2 d-block"></i>
                    Découpez le projet en phases (gros œuvre, second œuvre…) puis ajoutez des tâches.
                    <div className="small mt-1">Dès la première tâche, l'avancement du projet est calculé automatiquement.</div>
                </div>
            )}

            {/* ---- Phases ---- */}
            {planning.phases.map((p, i) => {
                const open = !collapsed.has(p.id);
                return (
                    <div key={p.id} className={`card mb-3 gp-phase ${p.enRetard ? "border-danger" : ""}`}>
                        <div className="card-header py-2 d-flex align-items-center flex-wrap">
                            <button className="btn btn-sm btn-link px-1 mr-1" onClick={() => toggle(p.id)}>
                                <i className={`fas fa-chevron-${open ? "down" : "right"}`}></i>
                            </button>
                            <strong className="mr-2">{i + 1}. {p.nom}</strong>
                            <span className="small text-muted mr-3">
                                {formatDate(p.dateDebut)} → {formatDate(p.dateFin)}
                                {p.budgetPrevu != null && <> · budget {formatAmount(p.budgetPrevu)}</>}
                                {" "}· {p.nbTerminees}/{p.nbTaches} tâche(s)
                            </span>
                            {p.enRetard && <span className="badge badge-danger mr-2">En retard</span>}
                            <div className="flex-grow-1 mr-3" style={{ minWidth: 120, maxWidth: 260 }}><ProgressBar value={p.avancement} enRetard={p.enRetard} /></div>
                            <div className="ml-auto text-nowrap">
                                <button className="btn btn-sm btn-light" title="Monter" disabled={i === 0} onClick={() => movePhase(p, -1)}><i className="fas fa-arrow-up"></i></button>
                                <button className="btn btn-sm btn-light mr-1" title="Descendre" disabled={i === planning.phases.length - 1} onClick={() => movePhase(p, 1)}><i className="fas fa-arrow-down"></i></button>
                                <button className="btn btn-sm btn-outline-secondary mr-1" title="Modifier" onClick={() => openPhase(p)}><i className="fas fa-edit"></i></button>
                                <button className="btn btn-sm btn-outline-danger" title="Supprimer" onClick={() => deletePhase(p)}><i className="fas fa-trash"></i></button>
                            </div>
                        </div>
                        {open && renderTaches(p.taches, p.id)}
                    </div>
                );
            })}

            {/* ---- Tâches sans phase ---- */}
            {(planning.tachesSansPhase.length > 0 || planning.phases.length > 0) && (
                <div className="card mb-3">
                    <div className="card-header py-2">
                        <button className="btn btn-sm btn-link px-1 mr-1" onClick={() => toggle("none")}>
                            <i className={`fas fa-chevron-${collapsed.has("none") ? "right" : "down"}`}></i>
                        </button>
                        <strong>Sans phase</strong> <span className="small text-muted">({planning.tachesSansPhase.length})</span>
                    </div>
                    {!collapsed.has("none") && renderTaches(planning.tachesSansPhase, "")}
                </div>
            )}

            <TacheFormModal projetId={projetId} tache={editingTache} defaultPhaseId={defaultPhaseId} openKey={openKey}
                phases={planning.phases} employes={employes} onSaved={() => load(true)} />

            {/* ---- Modal phase ---- */}
            <div className="modal fade" id={PHASE_MODAL}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5><i className="fas fa-layer-group mr-2"></i>{editingPhase ? "Modifier la phase" : "Nouvelle phase"}</h5>
                            <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Nom *</label>
                                <input name="nom" className={`form-control ${pi("nom")}`} value={phaseFormik.values.nom} onChange={phaseFormik.handleChange} onBlur={phaseFormik.handleBlur} placeholder="ex : Gros œuvre" />
                                <div className="invalid-feedback">{phaseFormik.errors.nom}</div>
                            </div>
                            <div className="form-row">
                                <div className="form-group col-md-4">
                                    <label>Début</label>
                                    <input type="date" name="dateDebut" className="form-control" value={phaseFormik.values.dateDebut} onChange={phaseFormik.handleChange} />
                                </div>
                                <div className="form-group col-md-4">
                                    <label>Fin</label>
                                    <input type="date" name="dateFin" className={`form-control ${pi("dateFin")}`} value={phaseFormik.values.dateFin} onChange={phaseFormik.handleChange} onBlur={phaseFormik.handleBlur} />
                                    <div className="invalid-feedback">{phaseFormik.errors.dateFin}</div>
                                </div>
                                <div className="form-group col-md-4">
                                    <label>Budget (DH)</label>
                                    <input type="number" step="0.01" min="0" name="budgetPrevu" className={`form-control ${pi("budgetPrevu")}`} value={phaseFormik.values.budgetPrevu} onChange={phaseFormik.handleChange} onBlur={phaseFormik.handleBlur} />
                                    <div className="invalid-feedback">{phaseFormik.errors.budgetPrevu}</div>
                                </div>
                            </div>
                            <div className="form-group mb-0">
                                <label>Description</label>
                                <textarea name="description" rows={2} className="form-control" value={phaseFormik.values.description} onChange={phaseFormik.handleChange} />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" data-dismiss="modal">Annuler</button>
                            <button type="button" className="btn btn-enregistrer" onClick={phaseFormik.handleSubmit} disabled={phaseFormik.isSubmitting}>Sauvegarder</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProjetPlanning;
